"use client";

import { LogOut, Menu, ShieldCheck, Sparkles } from "lucide-react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useEffect } from "react";
import { APP_NAME, NAV_SECTIONS, type NavItem } from "@/lib/constants";
import { useMe } from "@/hooks/use-me";
import { getSupabase } from "@/lib/supabase/client";
import { cn } from "@/lib/utils";
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";

/**
 * CMS sidebar — koyu, sıkı, operatöre yönelik.
 * 5 grup × 20 link. Frontend'in sidebar'ı ile aynı tasarım dilini paylaşır
 * (dark navy + accent vurgu) ama mode switcher, user-mode toggle, command
 * palette, system pulse ve content-creator helper'ları yok.
 */
export function Sidebar() {
  return (
    <aside className="hidden md:flex h-screen w-[240px] lg:w-[280px] shrink-0 flex-col bg-sidebar-bg text-sidebar-text">
      <SidebarBody />
    </aside>
  );
}

/* F13 cluster J — mobile sidebar drawer. CMS is admin-only but the
 * "no nav on phones" gap is still a real bug; on-call admins reach
 * /audit, /jobs and /recovery from their phone. */
export function MobileSidebar({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const pathname = usePathname();
  useEffect(() => {
    onOpenChange(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="left"
        className="w-[300px] max-w-[85vw] bg-sidebar-bg p-0 text-sidebar-text"
      >
        <SheetTitle className="sr-only">Navigasyon</SheetTitle>
        <SheetDescription className="sr-only">
          CMS navigasyonu ve hesap kontrolleri.
        </SheetDescription>
        <div className="flex h-full flex-col">
          <SidebarBody />
        </div>
      </SheetContent>
    </Sheet>
  );
}

export function MobileTopBar({
  onMenuClick,
}: {
  onMenuClick: () => void;
}) {
  return (
    <div className="sticky top-0 z-30 flex items-center gap-3 border-b border-border-subtle bg-bg-page/95 px-4 py-2.5 backdrop-blur md:hidden">
      <button
        type="button"
        onClick={onMenuClick}
        aria-label="Menüyü aç"
        className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-border-subtle bg-bg-card text-text-secondary transition-colors hover:bg-bg-card-hover hover:text-text-primary"
      >
        <Menu className="h-4 w-4" />
      </button>
      <Link href="/" className="flex items-center gap-2">
        <span className="flex h-7 w-7 items-center justify-center rounded-md bg-gradient-to-br from-accent to-accent-purple shadow-sm">
          <ShieldCheck className="h-3.5 w-3.5 text-white" strokeWidth={2.25} />
        </span>
        <span className="text-sm font-semibold tracking-tight text-text-primary">
          {APP_NAME}
        </span>
      </Link>
    </div>
  );
}

function SidebarBody() {
  const pathname = usePathname() ?? "";
  const me = useMe();
  const router = useRouter();

  const signOut = async () => {
    try {
      await getSupabase().auth.signOut();
    } catch {
      /* ignore */
    }
    router.replace("/login");
  };

  return (
    <div className="flex h-full w-full flex-col">
      {/* Brand block */}
      <Link
        href="/"
        className="flex items-center gap-3 px-5 py-5 transition-opacity hover:opacity-90"
      >
        <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-accent to-accent-purple shadow-md">
          <ShieldCheck className="h-5 w-5 text-white" strokeWidth={2.25} />
        </span>
        <span className="flex flex-col leading-tight">
          <span className="text-base font-semibold text-white tracking-tight">{APP_NAME}</span>
          <span className="text-[10px] font-medium uppercase tracking-[0.12em] text-sidebar-text-muted">
            Süper-Admin
          </span>
        </span>
      </Link>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-2 pb-3">
        <div className="space-y-5">
          {NAV_SECTIONS.map((section) => (
            <div key={section.label}>
              <p className="px-3 pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-sidebar-text-muted">
                {section.label}
              </p>
              <ul className="space-y-0.5">
                {section.items.map((item) => (
                  <SidebarItem key={item.href} item={item} pathname={pathname} />
                ))}
              </ul>
            </div>
          ))}
        </div>
      </nav>

      {/* Footer */}
      <div className="shrink-0 space-y-2 border-t border-sidebar-border p-3">
        <AccountChip
          email={me.data?.profile?.email ?? null}
          name={me.data?.profile?.display_name ?? null}
        />
        <button
          onClick={signOut}
          type="button"
          className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-xs font-medium text-sidebar-text-muted transition-colors hover:bg-sidebar-bg-hover hover:text-sidebar-text"
        >
          <LogOut className="h-3.5 w-3.5" />
          Çıkış yap
        </button>
      </div>
    </div>
  );
}

function SidebarItem({ item, pathname }: { item: NavItem; pathname: string }) {
  const Icon = item.icon;
  const active =
    item.href === "/" ? pathname === "/" : pathname === item.href || pathname.startsWith(`${item.href}/`);

  return (
    <li>
      <Link
        href={item.href}
        className={cn(
          "group relative flex items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium transition-colors duration-fast",
          active
            ? [
                "bg-sidebar-bg-active text-white",
                "before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2",
                "before:h-5 before:w-0.5 before:rounded-r before:bg-accent",
              ]
            : "text-sidebar-text hover:bg-sidebar-bg-hover hover:text-sidebar-text-hover",
        )}
        aria-current={active ? "page" : undefined}
      >
        <Icon className="h-4 w-4 shrink-0" />
        <span className="flex-1 truncate">{item.label}</span>
      </Link>
    </li>
  );
}

function AccountChip({ email, name }: { email: string | null; name: string | null }) {
  if (!email && !name) return null;
  const initial = (name?.trim()?.[0] || email?.trim()?.[0] || "?").toUpperCase();
  const primaryLabel = name?.trim() || email || "—";
  return (
    <div className="flex w-full items-center gap-2.5 rounded-md border border-white/[0.06] bg-white/[0.04] px-3 py-2.5">
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-accent/40 to-accent-purple/40 text-[11px] font-semibold uppercase text-white">
        {initial}
      </span>
      <span className="flex flex-1 flex-col leading-tight overflow-hidden">
        <span className="truncate text-xs font-medium text-sidebar-text">{primaryLabel}</span>
        <span className="font-mono text-[9px] uppercase tracking-[0.16em] text-sidebar-text-muted">
          super-admin
        </span>
      </span>
      <Sparkles className="h-3.5 w-3.5 shrink-0 text-accent/80" />
    </div>
  );
}
